<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  formatMediaByteSize,
  mediaQaActorLabel,
  mediaQaItemTitle,
  mediaQaRequirementLabel,
  mediaQaStatusLabel,
  mediaQaStatusTone,
} from '../utils/imovelShowFormatters';

defineProps({
  status: {
    type: String,
    default: '',
  },
  evidenceLabel: {
    type: String,
    default: '',
  },
  requirements: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
  loadingKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['decide']);

const mediaQaReasonDrafts = defineModel('mediaQaReasonDrafts', {
  type: Object,
  required: true,
});
const mediaQaEvidenceDrafts = defineModel('mediaQaEvidenceDrafts', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="grid gap-3 rounded-lais border border-lais-border-soft bg-white px-4 py-4 shadow-lais-card"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <p
          class="font-redHatDisplay text-[16px] font-semibold leading-tight text-lais-text"
        >
          QA de mídia
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{ evidenceLabel }}
        </p>
      </div>
      <LaisStatusBadge
        :label="mediaQaStatusLabel(status)"
        :tone="mediaQaStatusTone(status)"
        dot
      />
    </div>

    <div v-if="requirements.length" class="grid gap-2 sm:grid-cols-2">
      <div
        v-for="requirement in requirements"
        :key="requirement.key"
        class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-[12px] font-semibold text-lais-text">
            {{ mediaQaRequirementLabel(requirement) }}
          </span>
          <LaisStatusBadge
            :label="mediaQaStatusLabel(requirement.status)"
            :tone="mediaQaStatusTone(requirement.status)"
          />
        </div>
        <p
          v-if="requirement.blocker"
          class="mt-1 text-[12px] leading-relaxed text-lais-danger"
        >
          {{ requirement.blocker }}
        </p>
      </div>
    </div>

    <div v-if="items.length" class="grid gap-3">
      <div
        v-for="item in items"
        :key="item.id"
        class="grid gap-3 rounded-lais-sm border border-lais-border bg-white px-3 py-3"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-[13px] font-semibold text-lais-text">
              {{ mediaQaItemTitle(item) }}
            </p>
            <p class="mt-1 text-[11px] text-lais-text-muted">
              #{{ item.id }} · ordem {{ item.sortOrder }} ·
              {{ item.cover ? 'capa' : 'galeria' }} ·
              {{ item.origin || item.source || 'origem pendente' }}
            </p>
          </div>
          <LaisStatusBadge
            :label="mediaQaStatusLabel(item.status)"
            :tone="mediaQaStatusTone(item.status)"
          />
        </div>

        <div class="grid gap-2 text-[12px] text-lais-text-body sm:grid-cols-3">
          <span>{{ formatMediaByteSize(item.byteSize) }}</span>
          <span>
            {{ item.preflight?.status || 'preflight pendente' }}
          </span>
          <span>{{ item.rights?.status || 'direitos pendentes' }}</span>
        </div>

        <p
          v-if="item.lastError"
          class="rounded-lais-sm bg-lais-danger-50 px-3 py-2 text-[12px] text-lais-danger"
        >
          {{ item.lastError }}
        </p>

        <ul
          v-if="item.blockerLabels?.length"
          class="list-disc space-y-1 pl-4 text-[12px] text-lais-danger"
        >
          <li v-for="blocker in item.blockerLabels" :key="blocker">
            {{ blocker }}
          </li>
        </ul>

        <p class="text-[12px] text-lais-text-muted">
          {{ mediaQaActorLabel(item) }}
        </p>

        <div class="grid gap-2 sm:grid-cols-2">
          <input
            v-model="mediaQaReasonDrafts[item.id]"
            type="text"
            class="h-9 rounded-lais-sm border border-lais-border bg-white px-3 text-[12px] text-lais-text focus:border-lais-primary focus:outline-none"
            :placeholder="item.qa?.reason || 'Motivo'"
          />
          <input
            v-model="mediaQaEvidenceDrafts[item.id]"
            type="text"
            class="h-9 rounded-lais-sm border border-lais-border bg-white px-3 text-[12px] text-lais-text focus:border-lais-primary focus:outline-none"
            :placeholder="item.qa?.evidence || 'Evidência'"
          />
        </div>

        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            variant="primary"
            size="sm"
            icon="i-lucide-check"
            :is-loading="loadingKey === item.id + ':approved'"
            :disabled="!!loadingKey"
            @click="emit('decide', item, 'approved')"
          >
            Aprovar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            :is-loading="loadingKey === item.id + ':rejected'"
            :disabled="!!loadingKey"
            @click="emit('decide', item, 'rejected')"
          >
            Reprovar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            size="sm"
            icon="i-lucide-clock"
            :is-loading="loadingKey === item.id + ':pending'"
            :disabled="!!loadingKey"
            @click="emit('decide', item, 'pending')"
          >
            Pendenciar
          </LaisPrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>
